import { useRef, useState } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'

/** Repère interne de la signature — indépendant de la taille affichée. */
const LARGEUR = 360
const HAUTEUR = 200

/**
 * Signature au doigt. Les tracés sont conservés en coordonnées du repère
 * interne : la même signature se réimprime à l'identique sur le contrat, quelle
 * que soit la taille de la zone à l'écran.
 */
export function Signature({
  traces,
  onChange,
}: {
  traces: string[]
  onChange: (traces: string[]) => void
}) {
  const zone = useRef<HTMLDivElement>(null)
  const [enCours, setEnCours] = useState(false)

  const position = (evenement: ReactPointerEvent<HTMLDivElement>): [number, number] => {
    const cadre = evenement.currentTarget.getBoundingClientRect()
    return [
      Math.round(((evenement.clientX - cadre.left) / cadre.width) * LARGEUR),
      Math.round(((evenement.clientY - cadre.top) / cadre.height) * HAUTEUR),
    ]
  }

  const commencer = (evenement: ReactPointerEvent<HTMLDivElement>) => {
    evenement.currentTarget.setPointerCapture(evenement.pointerId)
    const [x, y] = position(evenement)
    setEnCours(true)
    onChange([...traces, `M${x} ${y}`])
  }

  const continuer = (evenement: ReactPointerEvent<HTMLDivElement>) => {
    if (!enCours) return
    const [x, y] = position(evenement)
    const dernier = traces[traces.length - 1]
    if (dernier === undefined) return
    onChange([...traces.slice(0, -1), `${dernier} L${x} ${y}`])
  }

  return (
    <>
      <div
        ref={zone}
        className="signature"
        onPointerDown={commencer}
        onPointerMove={continuer}
        onPointerUp={() => setEnCours(false)}
        onPointerCancel={() => setEnCours(false)}
        role="application"
        aria-label="Zone de signature"
      >
        <svg viewBox={`0 0 ${LARGEUR} ${HAUTEUR}`}>
          {traces.map((trace, index) => (
            <path
              key={index}
              d={trace}
              fill="none"
              stroke="var(--color-text)"
              strokeWidth={2.4}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </svg>
        {traces.length === 0 && <div className="invite">Signez avec le doigt</div>}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="btn btn-secondaire btn-centre"
          style={{ flex: 1 }}
          onClick={() => onChange([])}
          disabled={traces.length === 0}
        >
          Effacer
        </button>
      </div>
    </>
  )
}

/** Sérialise les tracés en fragment SVG rangé avec le contrat. */
export function signatureEnSvg(traces: string[]): string | null {
  if (traces.length === 0) return null
  return traces.map((trace) => `<path d="${trace}" />`).join('')
}
